import { useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';

import { useHuntStore } from '@/stores/hunt-store';
import { usePropertyStore } from '@/stores/property-store';
import { Button } from '@/components/common/Button';
import { DateField } from '@/components/forms/DateField';
import { useMediaQuery } from '@/hooks/use-media-query';
import { localTodayISO, toLocalISODate } from '@/utils/date-utils';

// M-08 (Defect Batch M): inward outline so focus ring is never clipped (ADR-035).
const INPUT_CLASS =
  'w-full min-w-0 box-border px-3 py-2.5 rounded-md bg-[var(--color-elevated)] border border-[var(--color-border)] text-body-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-placeholder)] focus:outline-2 focus:outline-[var(--color-accent)] focus:outline-offset-[-2px]';

function FieldLabel({
  htmlFor,
  children,
  required,
}: {
  htmlFor?: string;
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className="field-label"
    >
      {children}
      {required && <span className="text-[var(--color-accent)] ml-1">*</span>}
    </label>
  );
}

function FieldWrapper({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-1.5">{children}</div>;
}

const today = localTodayISO();
const tomorrow = toLocalISODate(new Date(Date.now() + 86400000));

export function HuntNewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { createHunt } = useHuntStore();
  const { properties } = usePropertyStore();
  // J-05: viewport breakpoint — "side-by-side" at sm (640px+), "stacked" below
  const isWide = useMediaQuery('(min-width: 640px)');

  // Pre-fill from query params (START HUNT flow) or defaults
  const initialSeasonId = searchParams.get('seasonId') ?? '';
  const initialPropertyId = searchParams.get('propertyId') ?? '';

  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(tomorrow);
  const [propertyId, setPropertyId] = useState(initialPropertyId || (properties[0]?.id ?? ''));
  const [companionInput, setCompanionInput] = useState('');
  const [companions, setCompanions] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs['name'] = 'Name is required';
    if (!startDate) errs['startDate'] = 'Start date is required';
    if (!endDate) errs['endDate'] = 'End date is required';
    if (startDate && endDate && endDate < startDate)
      errs['endDate'] = 'End date must be on or after start date';
    if (!propertyId) errs['propertyId'] = 'Property is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    setIsSaving(true);
    try {
      // Determine seasonId: from query param or derive from property's active season
      const seasonId = initialSeasonId || 'unassigned';
      const hunt = await createHunt({
        seasonId,
        propertyId,
        name: name.trim(),
        startDate,
        endDate,
        companions,
      });
      navigate(`/hunt/${hunt.id}`);
    } catch {
      setIsSaving(false);
    }
  }

  const addCompanion = useCallback(() => {
    const trimmed = companionInput.trim();
    if (!trimmed || companions.length >= 8) return;
    if (!companions.includes(trimmed)) {
      setCompanions((prev) => [...prev, trimmed]);
    }
    setCompanionInput('');
  }, [companionInput, companions]);

  const removeCompanion = useCallback((name: string) => {
    setCompanions((prev) => prev.filter((c) => c !== name));
  }, []);

  function handleCompanionKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCompanion();
    }
  }

  return (
    <div className="flex flex-col min-h-0 overflow-hidden" data-testid="hunt-new-page">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4">
        <Link
          to="/hunt"
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
          aria-label="Back"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </Link>
        <h1 className="font-display text-display-sm text-[var(--color-text-display)]">
          New Hunt Trip
        </h1>
      </div>

      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col flex-1 min-h-0"
        data-testid="hunt-form"
      >
        {/* Scrollable form body */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden flex flex-col gap-5 pb-4">
          {/* Name */}
          <FieldWrapper>
            <FieldLabel htmlFor="hunt-name" required>
              Trip Name
            </FieldLabel>
            <input
              id="hunt-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Thanksgiving weekend"
              className={INPUT_CLASS}
              data-testid="input-name"
            />
            {errors['name'] && (
              <p className="text-body-xs text-[var(--color-accent)]" data-testid="error-name">
                {errors['name']}
              </p>
            )}
          </FieldWrapper>

          {/* Date range — grid-cols-1 on mobile, 2 on sm+ (J-05) */}
          {/* hypothesis (fallback artifacts) — verify on device: gap-5 on mobile aligns vertical rhythm with form's gap-5; sm:gap-3 preserves tighter horizontal gap when side-by-side (L-02) */}
          <div
            className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-3 min-w-0"
            data-date-layout={isWide ? 'side-by-side' : 'stacked'}
          >
            <div className="min-w-0">
              <DateField
                id="hunt-start"
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                required
              />
              {errors['startDate'] && (
                <p className="text-body-xs text-[var(--color-accent)] mt-1">{errors['startDate']}</p>
              )}
            </div>

            <div className="min-w-0">
              <DateField
                id="hunt-end"
                label="End Date"
                value={endDate}
                min={startDate}
                onChange={setEndDate}
                required
              />
              {errors['endDate'] && (
                <p className="text-body-xs text-[var(--color-accent)] mt-1" data-testid="error-end-date">
                  {errors['endDate']}
                </p>
              )}
            </div>
          </div>

          {/* Property */}
          <FieldWrapper>
            <FieldLabel htmlFor="hunt-property" required>
              Property
            </FieldLabel>
            <select
              id="hunt-property"
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
              className={INPUT_CLASS}
              data-testid="select-property"
            >
              <option value="">— Select property —</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            {errors['propertyId'] && (
              <p className="text-body-xs text-[var(--color-accent)]" data-testid="error-property">
                {errors['propertyId']}
              </p>
            )}
          </FieldWrapper>

          {/* Companions */}
          <FieldWrapper>
            <FieldLabel>Companions</FieldLabel>
            {companions.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-1" data-testid="companion-chips">
                {companions.map((c) => (
                  <span
                    key={c}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-body-xs font-medium bg-[var(--color-elevated)] text-[var(--color-text-secondary)] border border-[var(--color-border)]"
                  >
                    {c}
                    <button
                      type="button"
                      onClick={() => removeCompanion(c)}
                      className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] leading-none"
                      aria-label={`Remove ${c}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={companionInput}
                onChange={(e) => setCompanionInput(e.target.value)}
                onKeyDown={handleCompanionKeyDown}
                placeholder="Add a name…"
                maxLength={50}
                disabled={companions.length >= 8}
                className={`${INPUT_CLASS} flex-1`}
                data-testid="input-companion"
              />
              <button
                type="button"
                onClick={addCompanion}
                disabled={!companionInput.trim() || companions.length >= 8}
                className="px-3 py-2.5 rounded-md bg-[var(--color-elevated)] border border-[var(--color-border)] text-body-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="add-companion-btn"
              >
                Add
              </button>
            </div>
            {companions.length >= 8 && (
              <p className="text-body-xs text-[var(--color-text-muted)]">Maximum 8 companions</p>
            )}
          </FieldWrapper>
        </div>

        {/* Sticky footer with action buttons */}
        <div
          className="sticky bottom-0 bg-bark-900/95 backdrop-blur-sm border-t border-bark-600 pb-safe px-4 py-3"
          data-testid="form-footer"
          data-layout-role="sticky-footer"
        >
          <div className="flex gap-3">
            <Button
              type="button"
              variant="ghost"
              className="flex-1"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSaving}
              className="flex-1"
              data-testid="submit-button"
            >
              {isSaving ? 'Saving…' : 'Create Hunt'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
